import { useCallback, useRef, useState } from 'react';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useDisconnect, useSignMessage } from 'wagmi';
import { ApiError } from '../api/client';
import { authAPI } from '../api/auth';
import type { AuthResponse } from '../api/types';
import { isWalletLoginConfigured } from './appkit-config';

function normalizeAddress(address: string): string {
  return address.trim().toLowerCase();
}

function mapWalletLoginError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const msg = err.message.toLowerCase();
    if (
      err.errorKey === 'auth.wallet_not_registered' ||
      msg.includes('wallet not registered') ||
      msg.includes('未绑定')
    ) {
      return 'auth.walletNotRegistered';
    }
    if (msg.includes('invalid wallet signature') || msg.includes('签名')) {
      return 'auth.walletSignatureFailed';
    }
    if (msg.includes('invalid username or password') || err.errorKey === 'auth.invalid_credentials') {
      return 'auth.walletLoginFailed';
    }
    return err.message;
  }
  if (err instanceof Error && err.message === 'Wallet login already in progress') {
    return 'auth.walletLoginInProgress';
  }
  return err instanceof Error ? err.message : fallback;
}

export function useWalletAuthLogin() {
  const { open } = useAppKit();
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflightRef = useRef<Promise<AuthResponse> | null>(null);

  const openWalletModal = useCallback(async () => {
    setError(null);
    if (!isWalletLoginConfigured()) {
      setError('Wallet login is not configured (missing VITE_REOWN_PROJECT_ID).');
      return;
    }
    await open();
  }, [open]);

  const loginWithConnectedWallet = useCallback(
    async (walletAddress: string): Promise<AuthResponse> => {
      const norm = normalizeAddress(walletAddress);
      if (inflightRef.current) {
        return inflightRef.current;
      }

      const run = (async () => {
        setBusy(true);
        setError(null);
        try {
          const challenge = await authAPI.walletChallenge({ address: norm });
          const signature = await signMessageAsync({ message: challenge.message });
          return await authAPI.walletLogin({
            address: norm,
            message: challenge.message,
            signature,
          });
        } catch (err) {
          await disconnect();
          throw err;
        } finally {
          setBusy(false);
        }
      })();

      inflightRef.current = run;
      try {
        return await run;
      } catch (err) {
        setError(mapWalletLoginError(err, 'Wallet login failed'));
        throw err;
      } finally {
        if (inflightRef.current === run) {
          inflightRef.current = null;
        }
      }
    },
    [disconnect, signMessageAsync],
  );

  const resetWalletAttempt = useCallback(() => {
    setError(null);
  }, []);

  return {
    address,
    isConnected,
    busy,
    error,
    openWalletModal,
    loginWithConnectedWallet,
    resetWalletAttempt,
    disconnect,
    isConfigured: isWalletLoginConfigured(),
  };
}
